import momentTz from 'moment-timezone';
import { views } from 'utils/cache/calendar/index';
import {
  MAX_CALENDAR_TIME,
  MIN_CALENDAR_TIME
} from 'utils/constants/calendar';
import { SERIES_UPCOMING_WEEKS } from 'utils/validators/planner';
import { View } from 'react-big-calendar';

/**
 * AXON-3585: the calendar's modes, named rather than expressed as a boolean.
 *
 * This component renders two different products — every department's ordinary
 * provider calendar, and the DT recurring template AXON-3578 introduced. They
 * were told apart by a `newDesign` flag threaded through 28 branches, which
 * made "what is a template?" something you had to reassemble by reading the
 * whole file.
 *
 * Level 2 of the Ops view adds a third mode, one weekday with clinicians in
 * the columns. A third arm on 28 two-way branches is how a fix lands in one
 * mode and silently misses the others, so the differences are gathered here
 * instead: one entry per mode, each a complete description of that calendar.
 */

/** The DT template spans midnight to midnight but opens scrolled to 8 AM. */
const DAY_START_12AM = momentTz('12:00am', 'h:mma').toDate();
const DAY_END_11PM = momentTz('11:59pm', 'h:mma').toDate();
const SCROLL_8AM = momentTz('8:00am', 'h:mma').toDate();

export type CalendarModeName = 'legacy' | 'template' | 'dayGrid';

export interface CalendarMode {
  /** Which react-big-calendar views the user can reach. */
  views: View[];
  /** Null disables drilling from month into day. */
  drilldownView: View | null | undefined;
  minTime: Date;
  maxTime: Date;
  scrollToTime?: Date;
  /** Minutes per row, and rows per labelled slot. */
  step: number;
  timeslots: number;
  /**
   * Weeks to look past the displayed range. The template is a standing week
   * rather than a calendar, so it reaches far enough forward to catch series
   * that are confirmed but have not started yet; those occurrences are folded
   * back onto the displayed week. An ordinary calendar wants only what it shows.
   */
  lookAheadWeeks: number;
  /**
   * Whether events are folded into one standing week — recurring care sessions
   * and availability slots only — or shown as the raw calendar.
   */
  foldsIntoStandingWeek: boolean;
  /** Whether this mode publishes its totals to the profile sidebar. */
  publishesTemplateSummary: boolean;
  /**
   * Whether this reads as the recurring template rather than an ordinary
   * calendar. Passed down to the event and column-header components, which
   * style themselves from it.
   */
  isTemplate: boolean;
  /** Whether column headers carry the booked-over-target session figures. */
  showsSessionHeaders: boolean;
  /** Whether the toolbar carries the template's title and actions. */
  showsTemplateToolbar: boolean;
  /** Whether a 30-minute appointment gets the compact treatment. */
  compactsShortAppointments: boolean;
}

export const CALENDAR_MODES: Record<CalendarModeName, CalendarMode> = {
  legacy: {
    views: [views.DAY, views.WEEK, views.MONTH] as View[],
    drilldownView: undefined,
    minTime: MIN_CALENDAR_TIME,
    maxTime: MAX_CALENDAR_TIME,
    scrollToTime: undefined,
    step: 15,
    timeslots: 4,
    lookAheadWeeks: 0,
    foldsIntoStandingWeek: false,
    publishesTemplateSummary: false,
    isTemplate: false,
    showsSessionHeaders: false,
    showsTemplateToolbar: false,
    compactsShortAppointments: false
  },
  template: {
    views: [views.WEEK] as View[],
    drilldownView: null,
    minTime: DAY_START_12AM,
    maxTime: DAY_END_11PM,
    scrollToTime: SCROLL_8AM,
    step: 30,
    timeslots: 1,
    lookAheadWeeks: SERIES_UPCOMING_WEEKS,
    foldsIntoStandingWeek: true,
    publishesTemplateSummary: true,
    isTemplate: true,
    showsSessionHeaders: true,
    showsTemplateToolbar: true,
    compactsShortAppointments: true
  },
  /**
   * AXON-3585 level 2: one weekday at one clinic, clinicians in the columns.
   *
   * Every axis that decides how a slot READS is identical to `template` — the
   * two are the same template seen through a different window, and a scheduler
   * moving between them should not have to learn it twice. Only the axes that
   * describe the window itself differ: a single day rather than a week, no
   * sidebar summary to publish, and headers carrying clinicians instead of
   * weekdays (the day grid supplies those as rbc `resources`).
   */
  dayGrid: {
    views: [views.DAY] as View[],
    drilldownView: null,
    minTime: DAY_START_12AM,
    maxTime: DAY_END_11PM,
    scrollToTime: SCROLL_8AM,
    step: 30,
    timeslots: 1,
    lookAheadWeeks: SERIES_UPCOMING_WEEKS,
    foldsIntoStandingWeek: true,
    publishesTemplateSummary: false,
    isTemplate: true,
    showsSessionHeaders: false,
    showsTemplateToolbar: false,
    compactsShortAppointments: true
  }
};
